/**
 * HiraNova World — i18n Configuration
 *
 * react-i18next + JSON locales + tarayıcı dili tespiti.
 * Çeviri eksik olan key'ler TR'ye fallback eder.
 *
 * URL stratejisi (App.tsx tarafından yönetilir):
 *   TR (varsayılan): /genel-plan, /villa/suite-1, ...
 *   EN/DE/RU/AR:     /en/genel-plan, /de/villa/suite-1, ...
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import tr from '../locales/tr.json'
import en from '../locales/en.json'
import de from '../locales/de.json'
import ru from '../locales/ru.json'
import ar from '../locales/ar.json'
import fr from '../locales/fr.json'
import zh from '../locales/zh.json'

export type Lang = 'tr' | 'en' | 'de' | 'ru' | 'ar' | 'fr' | 'zh'

export const LANGUAGES: { code: Lang; name: string; nativeName: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe',  dir: 'ltr' },
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'de', name: 'German',  nativeName: 'Deutsch', dir: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', dir: 'ltr' },
  { code: 'ar', name: 'Arabic',  nativeName: 'العربية', dir: 'rtl' },
  { code: 'fr', name: 'French',  nativeName: 'Français', dir: 'ltr' },
  { code: 'zh', name: 'Chinese', nativeName: '中文',      dir: 'ltr' },
]

export const DEFAULT_LANG: Lang = 'tr'
export const SUPPORTED_LANGS: Lang[] = LANGUAGES.map(l => l.code)

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      tr: { translation: tr },
      en: { translation: en },
      de: { translation: de },
      ru: { translation: ru },
      ar: { translation: ar },
      fr: { translation: fr },
      zh: { translation: zh },
    },
    /* FR/ZH henüz kısmi çeviri: eksik anahtarlar Türkçe değil İNGİLİZCE'ye düşer. */
    fallbackLng: { fr: ['en', DEFAULT_LANG], zh: ['en', DEFAULT_LANG], default: [DEFAULT_LANG] },
    supportedLngs: SUPPORTED_LANGS,
    interpolation: { escapeValue: false }, // React zaten escape ediyor
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0,
      caches: ['localStorage'],
      lookupLocalStorage: 'hnw_lang',
    },
    returnObjects: true,                   // arrays / nested objects için
  })

// Dil değişince <html lang> ve <html dir> güncellenir
const applyHtmlAttrs = (lng: string) => {
  const meta = LANGUAGES.find(l => l.code === lng)
  if (meta) {
    document.documentElement.lang = meta.code
    document.documentElement.dir = meta.dir
  }
}
applyHtmlAttrs(i18n.language || DEFAULT_LANG)
i18n.on('languageChanged', applyHtmlAttrs)

export default i18n

/** URL pathname'inden dil prefix'i tespit eder.
 * /en/genel-plan → 'en', /genel-plan → 'tr' (default) */
export const getLangFromPath = (pathname: string): Lang => {
  const match = pathname.match(/^\/(en|de|ru|ar|fr|zh)(\/|$)/)
  return (match?.[1] as Lang) ?? DEFAULT_LANG
}

/** Verilen path'i istenen dil prefix'iyle yeniden oluşturur.
 * '/en/genel-plan' + 'de' → '/de/genel-plan'
 * '/genel-plan' + 'tr' → '/genel-plan' */
export const pathWithLang = (pathname: string, targetLang: Lang): string => {
  const stripped = pathname.replace(/^\/(en|de|ru|ar|fr|zh)(?=\/|$)/, '') || '/'
  if (targetLang === DEFAULT_LANG) return stripped
  return `/${targetLang}${stripped === '/' ? '' : stripped}`
}
